import React, { useEffect, useState } from "react";
import { ClockCounterClockwise, X } from "@phosphor-icons/react";
import api from "../lib/api";
import ReadAloudButton from "./ReadAloudButton";

// Tiny "Previous days" pill that opens a slide-down drawer listing the
// last N persisted entries for one of the three daily cards. Backed by
// the new /api/today/recent endpoint, which only returns dates BEFORE
// today (the live card already shows today).
//
// Props:
//   kind   — "good-news" | "person-of-day" | "on-this-day-bh"
//   label  — short word for the pill, e.g. "Previous days"
//   render — function(item) → { headline, body, dateText } so each
//            kind can format its own item layout.
//   pillClass — Tailwind classes for the trigger pill so the colour
//               matches the parent card.
//   readAloudVariant — "dark" | "light" — pass through to ReadAloudButton.
export default function PreviousDaysDrawer({
  kind,
  label = "Previous days",
  render,
  pillClass = "bg-ink/10 text-ink",
  readAloudVariant = "dark",
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || items != null) return undefined;
    let alive = true;
    setLoading(true);
    api
      .get("/today/recent", { params: { kind, limit: 7 } })
      .then((r) => { if (alive) setItems(r.data?.items || []); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, items, kind]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`previous-days-open-${kind}`}
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] tracking-[0.18em] uppercase font-bold ${pillClass} hover:opacity-90 active:scale-95 transition`}
      >
        <ClockCounterClockwise size={11} weight="bold" />
        {label}
      </button>

      {open && (
        <div
          data-testid={`previous-days-drawer-${kind}`}
          className="fixed inset-0 z-[120] bg-stone-950/70 backdrop-blur-sm flex items-end sm:items-center justify-center px-3 sm:px-6 py-3"
          onClick={() => setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xl max-h-[85vh] overflow-y-auto rounded-3xl bg-cream text-ink shadow-2xl"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between bg-cream/95 backdrop-blur border-b border-ink/10 px-5 py-3">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-ink/70 flex items-center gap-1.5">
                <ClockCounterClockwise size={12} weight="bold" /> {label}
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid={`previous-days-close-${kind}`}
                className="w-8 h-8 grid place-items-center rounded-full hover:bg-ink/10 active:scale-90 transition"
                aria-label="Close"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
            {loading && (
              <div className="p-10 text-center text-sm text-ink/50">Loading…</div>
            )}
            {!loading && items && items.length === 0 && (
              <div className="p-10 text-center text-sm text-ink/50">
                No previous days yet — check back tomorrow.
              </div>
            )}
            {!loading && items && items.length > 0 && (
              <ul className="divide-y divide-ink/10">
                {items.map((it) => {
                  const v = render(it) || {};
                  return (
                    <li
                      key={it.date}
                      data-testid={`previous-days-item-${kind}-${it.date}`}
                      className="px-5 py-4"
                    >
                      <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-ink/50">
                        {v.dateText || it.date}
                      </div>
                      <div className="mt-1 font-heading text-lg leading-snug">
                        {v.headline}
                      </div>
                      {v.body && (
                        <p className="mt-1.5 text-sm leading-relaxed text-ink/80">
                          {v.body}
                        </p>
                      )}
                      <div className="mt-2">
                        <ReadAloudButton
                          text={`${v.headline || ""}. ${v.body || ""}`}
                          testid={`previous-days-read-${kind}-${it.date}`}
                          variant={readAloudVariant}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// Pretty date label like "Mon · May 18". Falls back to ISO if parsing fails.
export function prettyDate(iso) {
  try {
    const d = new Date(iso + "T12:00:00Z");
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  } catch (_) {
    return iso;
  }
}
